import React, { memo, useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  BreadcrumbItem as BreadcrumbItemType,
  BreadcrumbsProps,
} from './types';
import { formatTitle } from './utils';
import BreadcrumbItem from './BreadcrumbItem';
import { useAppSelector } from '@store/hooks';
import { selectGlobal } from '@store/slices/global/global';
import { selectUsers } from '@store/slices/users/usersSlice';

// the crumb the See Rides action opens, by route or by router state
const RIDE_DETAILS_TITLE = 'Ride Details';

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ entityName }) => {
  const location = useLocation();
  const params = useParams();
  const { currentPath } = useAppSelector(selectGlobal);
  const { isContractorVersion } = useAppSelector(selectUsers);
  const createPath = useCallback(
    (segments: string[], index: number): string =>
      '/' + segments.slice(0, index + 1).join('/'),
    []
  );

  // "See Rides" either routes to /see-rides or reloads the same route with the
  // drill-through ids in the state, depending on the report
  const isSeeRidesView = useMemo(() => {
    const state = location.state as {
      memberIds?: unknown;
      segmentId?: unknown;
    } | null;

    return (
      (Array.isArray(state?.segmentId) && state.segmentId.length > 0) ||
      (Array.isArray(state?.memberIds) && state.memberIds.length > 0)
    );
  }, [location.state]);

  const rawPathSegments = useMemo(
    () => location.pathname.split('/').filter(Boolean),
    [location.pathname]
  );

  const breadcrumbs = useMemo((): BreadcrumbItemType[] => {
    const items: BreadcrumbItemType[] = [
      { id: 'home', title: 'Home', path: '/' },
    ];

    if (location.pathname === '/') return items;

    const activeSection = rawPathSegments[1];
    if (entityName && activeSection) {
      const entityInformationPath = [
        rawPathSegments[0],
        'information',
        ...rawPathSegments.slice(2),
      ].join('/');

      return [
        ...items,
        {
          id: 'entity-name',
          title: entityName,
          path: `/${entityInformationPath}`,
          preserveTitle: true,
          clickable: true,
        },
        {
          id: `${activeSection}-section`,
          title: formatTitle(activeSection),
          path: createPath(rawPathSegments, 1),
        },
      ];
    }

    const pathSegments = location.pathname
      .split('/')
      .filter(Boolean)
      .filter((segment) => segment !== params.id);

    for (let index = 0; index < pathSegments.length; index++) {
      let segment = pathSegments[index];

      let path = createPath(pathSegments, index);
      if (segment === 'reporting' && pathSegments[index + 1]) {
        path = `/reporting/${pathSegments[index + 1]}`;
      }
      items.push({
        id: `${segment}-${index}`,
        title:
          segment === 'rideshare-reports' && isContractorVersion
            ? 'Contractor Reports'
            : segment === 'see-rides'
              ? RIDE_DETAILS_TITLE
              : formatTitle(segment),
        path,
      });
    }

    // the reports that drill through in place keep their url and carry the
    // picked ids in the router state, so the crumb is added from that state
    if (
      isSeeRidesView &&
      pathSegments[pathSegments.length - 1] !== 'see-rides'
    ) {
      items.push({
        id: 'see-rides',
        title: RIDE_DETAILS_TITLE,
        path: `${location.pathname}${location.search}`,
      });
    }

    // whichever way it was opened, the report behind it goes back to its table
    const parent = items[items.length - 2];
    if (items[items.length - 1]?.title === RIDE_DETAILS_TITLE && parent) {
      parent.clickable = true;
      if (parent.path === location.pathname && location.search) {
        parent.path = `${location.pathname}${location.search}`;
      }
    }

    return items;
  }, [
    location.pathname,
    location.search,
    isSeeRidesView,
    entityName,
    params,
    rawPathSegments,
    createPath,
    currentPath,
    isContractorVersion,
  ]);

  return (
    <ul className="breadcrumbs" data-test-id="breadcrumbs">
      {breadcrumbs.map((item, index, arr) => (
        <BreadcrumbItem
          key={item.id}
          item={item}
          isFirst={index === 0}
          isLast={index === arr.length - 1}
          showDivider={index !== arr.length - 1}
        />
      ))}
    </ul>
  );
};

export default memo(Breadcrumbs);
